import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PanoramaViewer from './PanoramaViewer';

interface Environment {
  id: string;
  name: string;
  category: string;
  imageUrl: string;
  description: string;
  features: string[];
}

const EnvironmentPanoramaTabs: React.FC = () => {
  const [activeEnvironment, setActiveEnvironment] = useState<string>('theatre-room');

  const environments: Environment[] = [
    {
      id: 'theatre-room',
      name: 'Theatre Room',
      category: 'Indoor Creative Space',
      imageUrl: '/images/Theatre_Room.jpg',
      description: 'A dedicated performance space where children explore drama, storytelling, and creative expression through theatrical play.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
        id: 'nap-room',
        name: 'Nap Room',
        category: 'Indoor Resting Space',
        imageUrl: '/images/Nap_Room.jpg',
        description: 'A quiet and cozy space for children to rest and recharge.',
        features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
      id: 'visual-arts-room',
      name: 'Visual Arts Room',
      category: 'Creative Studio',
      imageUrl: '/images/Art_Room.jpg',
      description: 'An inspiring art studio equipped with materials and spaces for painting, drawing, sculpting, and various artistic endeavors.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    },
    {
      id: 'jungle-play-hallway',
      name: 'Jungle Play Hallway',
      category: 'Adventure Zone',
      imageUrl: '/images/Jungle_Room.jpg',
      description: 'An exciting jungle-themed play environment that encourages exploration, adventure, and nature-based learning experiences.',
      features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4']
    }
  ];

  const activeEnvironmentData = environments.find(e => e.id === activeEnvironment) || environments[0];

  const tabVariants = {
    inactive: { 
      backgroundColor: '#e5e7eb',
      color: '#6b7280',
      scale: 0.95
    },
    active: { 
      backgroundColor: '#1e3a8a',
      color: '#ffffff',
      scale: 1
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-6">
          {environments.map((environment) => (
            <motion.button
              key={environment.id}
              onClick={() => setActiveEnvironment(environment.id)}
              className={`px-3 py-2 md:px-4 md:py-3 rounded-lg font-semibold text-xs md:text-sm transition-all duration-300 shadow-lg min-w-[100px] md:min-w-[140px] ${
                activeEnvironment === environment.id
                  ? 'bg-dblue text-white shadow-lg'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              variants={tabVariants}
              initial="inactive"
              animate={activeEnvironment === environment.id ? "active" : "inactive"}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="text-center">
                <div className="font-bold leading-tight">
                  {environment.name}
                </div>
                <div className="text-xs opacity-80 mt-1">{environment.category}</div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Active Environment Info */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeEnvironment}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center mb-6"
          >
            <h2 className="text-3xl font-bold text-dblue mb-2">
              {activeEnvironmentData.name}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-4">
              {activeEnvironmentData.description}
            </p>
            {/*
            <div className="flex flex-wrap justify-center gap-2">
              {activeEnvironmentData.features.map((feature, index) => (
                <span
                  key={index}
                  className="bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full"
                >
                  {feature}
                </span>
              ))}
            </div>
            */}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Panorama Viewer */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeEnvironment}
          variants={contentVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="max-w-5xl mx-auto"
        >
          <PanoramaViewer
            imageUrl={activeEnvironmentData.imageUrl}
            title={activeEnvironmentData.name}
            description={`Explore our ${activeEnvironmentData.name.toLowerCase()} - ${activeEnvironmentData.description.toLowerCase()}`}
            autoRotate={-2}
            className="w-full"
          />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Hints */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-8 text-center"
      >
        <div className="bg-green-50 rounded-lg p-6 max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-3">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-green-600">Environment Tours</span>
          </div>
          <p className="text-sm text-gray-600">
            Click the environment tabs above to explore our specialized learning and play spaces. 
            Each area is designed to support different aspects of child development and creativity.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default EnvironmentPanoramaTabs;
